import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import RequisitesForm from "../Documents/RequisitesForm";
import { activateAward, fetchAwards } from "../../helpers/awardsApi";
import { formatDate } from "../../helpers/dates";
import { fetchProfile, saveProfile, readAchievements } from "../../helpers/profileApi";
import GrantsBoard from "./GrantsBoard";
import PurchaseSummary from "./PurchaseSummary";
import { ROLES } from "../../helpers/roles";

/**
 * Личный кабинет: профиль участника.
 *
 * Свёрстан по прототипу fw-lk-cabinet.vercel.app/profile.html, согласованному
 * с Максимом 20.08.2026. Порядок вкладок не случайный: «Мои награды»
 * открывается первой, потому что за ней человек сюда и приходит после покупки.
 * Реквизиты и безопасность — в конце: они нужны единицам и только когда дошло
 * до договора.
 */

const PUBLIC = process.env.PUBLIC_URL || "";

const TABS = [
  { key: "awards", title: "Мои награды" },
  { key: "data", title: "Личные данные" },
  { key: "roles", title: "Мои роли" },
  { key: "requisites", title: "Реквизиты" },
  { key: "security", title: "Безопасность" },
];

/* Соцсети в том виде, в каком их отдаёт платформа. */
const SOCIAL_FIELDS = [
  { key: "vk", title: "ВКонтакте" },
  { key: "telegram", title: "Telegram" },
  { key: "whatsapp", title: "WhatsApp" },
  { key: "instagram", title: "Instagram" },
  { key: "youtube", title: "YouTube" },
  { key: "fb", title: "Facebook" },
  { key: "twitter", title: "Twitter" },
  { key: "viber", title: "Viber" },
];

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const GiftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 12v9H4v-9" />
    <path d="M2 7h20v5H2z" />
    <path d="M12 21V7" />
    <path d="M12 7S9.5 3 7.5 3a2.5 2.5 0 0 0 0 5" />
    <path d="M12 7s2.5-4 4.5-4a2.5 2.5 0 0 1 0 5" />
  </svg>
);

/* Картинки наград лежат в проекте: без них карточка выглядит пустой
   заготовкой, а человек ждёт увидеть то, что купил. */
const AWARD_PICS = {
  license: `${PUBLIC}/fw/award-license.webp`,
  flowerbox: `${PUBLIC}/fw/award-flowerbox.webp`,
};

const AwardCard = ({ award, onActivate, busy }) => {
  const active = award.status === "active";
  const pic = award.image || AWARD_PICS[award.kind] || null;
  /* Flower Box активируется вместе с лицензией — одним действием выдаются
     обе награды. Отдельная кнопка у коробки сбивала бы с толку: человек
     нажал бы её и не понял, почему «активировалось» ещё что-то. */
  const ownButton = !active && award.activates_with !== "license";

  return (
    <article className={`fw-card fw-award${pic ? "" : " no-pic"}`}>
      {pic ? <img className="award-pic" src={pic} alt={award.title} /> : null}

      <div className="award-body">
        <div className="award-top">
          <b>{award.title}</b>
          {active ? (
            <span className="fw-tag ok">
              <CheckIcon />
              Активна
            </span>
          ) : (
            <span className="fw-tag wait">Не активирована</span>
          )}
        </div>

        <p className="desc">{award.description}</p>

        {award.paid_at ? <span className="when">Оплачено {formatDate(award.paid_at)}</span> : null}

        <div className="acts">
          {ownButton && (
            <button type="button" className="fw-btn" disabled={busy} onClick={() => onActivate(award.id)}>
              {busy ? "Минуту…" : "Активировать"}
            </button>
          )}
          {!active && award.activates_with === "license" && (
            <span className="fw-award-hint">Активируется вместе с лицензией</span>
          )}
          <Link className="fw-btn ghost" to={`${PUBLIC}/instruction`}>
            {active ? "Что это даёт" : "Как активировать"}
          </Link>
        </div>
      </div>
    </article>
  );
};

const ProfilePage = () => {
  const [tab, setTab] = useState("awards");

  const [awards, setAwards] = useState([]);
  const [entitlement, setEntitlement] = useState(null);
  /* Заявки без подтверждённой оплаты. Нужны ровно для одного: не звать
     «купите лицензию» того, кто её уже купил и ждёт подтверждения. */
  const [pendingPayments, setPendingPayments] = useState([]);
  const [awardsLoading, setAwardsLoading] = useState(true);
  const [awardsError, setAwardsError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [achievements, setAchievements] = useState(null);
  // Весь ответ ручки: из него заполняются поля личных данных.
  const [me, setMe] = useState(null);
  const [socials, setSocials] = useState(null);
  // Ошибку загрузки профиля показываем прямо в разделе: пустые поля без
  // объяснения читаются как «у вас ничего не заполнено», хотя данные
  // могли просто не приехать.
  const [meError, setMeError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  /* Сохранение анкеты. Читаем значения из полей напрямую, а не держим их в
     состоянии: поля неуправляемые (defaultValue + key), потому что значения
     приходят асинхронно и управляемый вариант затирал бы ввод в момент
     ответа платформы. Для формы из шести полей это честнее, чем городить
     синхронизацию. */
  const onSaveProfile = async () => {
    const val = (id) => {
      const el = document.getElementById(id);
      return el ? el.value.trim() : "";
    };

    setSaving(true);
    setSaveMsg(null);
    try {
      await saveProfile({
        user: {
          name_last: val("fw-last-name"),
          name_first: val("fw-first-name"),
          name_middle: val("fw-middle-name"),
          country: val("fw-country"),
          town: val("fw-city"),
        },
        socials: {
          telegram: val("fw-telegram").replace(/^@/, ""),
          phone: val("fw-phone"),
        },
      });

      /* Перечитываем профиль, а не верим отправленному: платформа могла
         почистить значения своими правилами, и показывать надо то, что она
         действительно записала. */
      const fresh = await fetchProfile();
      setMe((fresh && fresh.user) || null);
      setSocials((fresh && fresh.socials) || null);
      setSaveMsg({ ok: true, text: "Сохранено" });
    } catch (e) {
      setSaveMsg({ ok: false, text: (e && e.message) || "не удалось сохранить" });
    } finally {
      setSaving(false);
    }
  };

  // Имя и почта приходят при входе; роль — из профиля, когда появится ручка.
  const stored = localStorage.getItem("Name") || "";
  const storedName = stored === "Emay Walter" ? "" : stored;
  const role = localStorage.getItem("role") || "Гость";

  /* Имя и почта — из профиля платформы, локальные значения запасные.
     Раньше почту подменяло имя из localStorage, если почты там не было,
     и в шапке имя стояло дважды: крупно и подписью под собой. */
  const email = (me && me.email) || localStorage.getItem("email") || "";
  const name =
    (me ? [me.name_first, me.name_last].filter(Boolean).join(" ").trim() : "") ||
    storedName;

  const load = () =>
    fetchAwards().then((data) => {
      setAwards(Array.isArray(data.awards) ? data.awards : []);
      setEntitlement(data.entitlement || null);
      setPendingPayments(Array.isArray(data.pending_payments) ? data.pending_payments : []);
    });

  const activate = async (id) => {
    setBusyId(id);
    try {
      await activateAward(id);
      await load();
    } catch (err) {
      setAwardsError(String(err.message || err));
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    let alive = true;

    // Достижения приходят с платформы; не пришли — остаются прочерки.
    fetchProfile()
      .then((data) => {
        if (!alive) return;
        setAchievements(readAchievements(data));
        setMe((data && data.user) || null);
        setSocials((data && data.socials) || null);
        setMeError(null);
      })
      .catch((e) => {
        if (alive) setMeError(e && e.message ? e.message : "не удалось получить данные");
      });

    fetchAwards()
      .then((data) => {
        if (!alive) return;
        setAwards(Array.isArray(data.awards) ? data.awards : []);
        setEntitlement(data.entitlement || null);
        setPendingPayments(Array.isArray(data.pending_payments) ? data.pending_payments : []);
      })
      .catch((err) => {
        if (alive) setAwardsError(String(err.message || err));
      })
      .finally(() => {
        if (alive) setAwardsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="fw-page">
      {/* Итог покупки для того, кто пришёл с оплаты. Компонент сам решает,
          показываться ли: на обычном заходе в профиль он ничего не рисует. */}
      <PurchaseSummary />

      <section className="fw-card fw-head">
        <span className="big">{(name || email || "?").slice(0, 1).toUpperCase()}</span>
        <div>
          <h1>{name || "Мой профиль"}</h1>
          <div className="meta">{email || "Личные данные, роли, награды и реквизиты — в одном месте."}</div>
        </div>
        <div className="side-act">
          <span className="fw-tag off">Текущая роль: {role}</span>
        </div>
      </section>

      {/* Достижения и кошелёк — над вкладками, потому что это единственное,
          ради чего в профиль заходят повторно. Анкету заполняют один раз,
          а сюда смотрят каждый раз. Цифры взяты из FairyTON по составу,
          согласованному с Максимом 22.08.2026; данных за ними пока нет —
          ручек на платформе не существует, поэтому показываем прочерки,
          а не выдуманные числа. */}
      <section className="fw-card fw-achieve">
        <div className="fw-achieve-list">
          {[
            { key: "level", title: "Уровень" },
            { key: "locations", title: "Мои локации" },
            { key: "quests", title: "Пройдено квестов" },
            { key: "friends", title: "Друзья" },
          ].map((a) => (
            <div key={a.key}>
              {/* Прочерк, а не ноль: ноль читается как «у вас ничего нет»,
                  хотя число могло просто не прийти. */}
              <b>
                {achievements && achievements[a.key] !== null
                  ? achievements[a.key].toLocaleString("ru-RU")
                  : "—"}
              </b>
              <span>{a.title}</span>
            </div>
          ))}
        </div>

        <Link className="fw-btn ghost fw-achieve-wallet" to={`${PUBLIC}/wallet`}>
          Кошелёк
        </Link>
      </section>

      <div className="fw-ptabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            className="fw-ptab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.title}
          </button>
        ))}
      </div>

      {tab === "awards" && (
        <div>
          <h2 className="fw-title">Мои награды</h2>
          <p className="fw-sub">
            Всё, что вы получили в проекте: лицензии, коробки и цифровые карточки.
            Награды появляются здесь автоматически после оплаты.
          </p>

          {awardsLoading && <p className="fw-sec-sub">Загрузка…</p>}

          {!awardsLoading && !awardsError && awards.length > 0 && (
            <section className="fw-awards">
              {awards.map((a) => (
                <AwardCard key={a.key ?? a.id ?? a.title} award={a} busy={busyId === a.id} onActivate={activate} />
              ))}
            </section>
          )}

          {!awardsLoading && entitlement && entitlement.awards > 0 && (
            <>
              <div className="fw-sec-head">
                <h2>Что это даёт</h2>
              </div>
              <p className="fw-sec-sub">{entitlement.hint}</p>
            </>
          )}

          {/* Ошибка и «наград нет» — разные состояния. Раньше показывались
              оба сразу: человек читал «наград пока нет», хотя на деле мы
              просто не смогли их получить. */}
          {!awardsLoading && awardsError && (
            <section className="fw-card fw-empty">
              <span className="ic">
                <GiftIcon />
              </span>
              <b>Не удалось загрузить награды</b>
              <p>{awardsError}. Обновите страницу; если повторится — напишите в поддержку.</p>
            </section>
          )}

          {/* Купил, но оплата не подтверждена. Предлагать такому человеку
              «купить лицензию» нельзя — он уже заплатил; ему нужен номер
              заказа и понимание, ждать или идти в поддержку. */}
          {!awardsLoading && !awardsError && awards.length === 0 && pendingPayments.length > 0 && (
            <section className="fw-card fw-empty">
              <span className="ic">
                <GiftIcon />
              </span>
              <b>Оплата ещё не подтверждена</b>
              <p>
                Заказ {pendingPayments[0].order_id || pendingPayments[0].payment_id || ""} мы
                видим, но подтверждения от банка по нему нет. Обычно оно приходит
                за пару минут. Если ждёте дольше четверти часа — напишите в
                поддержку и назовите этот номер: платёж сверят вручную.
              </p>
            </section>
          )}

          {!awardsLoading && !awardsError && awards.length === 0 && pendingPayments.length === 0 && (
            <section className="fw-card fw-empty">
              <span className="ic">
                <GiftIcon />
              </span>
              <b>Наград пока нет</b>
              <p>
                Купите Юбилейную лицензию — она придёт сюда вместе с подарочным
                Flower Box, и здесь же их можно будет активировать.
              </p>
              <a className="fw-btn" href="https://birthday.fairyland.world/">
                Как получить лицензию
              </a>
            </section>
          )}
        </div>
      )}

      {tab === "awards" && (
        <>
          <div className="fw-sec-head">
            <h2>Награды из миров</h2>
          </div>
          <p className="fw-sec-sub">
            Всё, что вы получили в играх: цифровые и коллекционные награды,
            фарм-награды и билеты. Нажмите карточку, чтобы рассмотреть.
          </p>
          <GrantsBoard />
        </>
      )}

      {tab === "data" && (
        <div>
          <h2 className="fw-title">Личные данные</h2>
          <p className="fw-sub">Эти данные видите только вы и сотрудники проекта.</p>

          {meError ? (
            <section className="fw-card fw-auth-note mb-3">
              Не удалось загрузить данные профиля: {meError}. Поля ниже пустые
              не потому, что вы их не заполняли — платформа не ответила.
              Обновите страницу; если не поможет, напишите в поддержку.
            </section>
          ) : null}

          <section className="fw-card fw-box">
            <h3>Основное</h3>
            <p className="fw-note">Имя показывается в кабинете и подставляется в документы.</p>
            <div className="fw-grid2">
              {/* Значения приходят с платформы. key завязан на id профиля:
                  без него React оставил бы поля пустыми — defaultValue
                  читается только при первой отрисовке, а профиль приезжает
                  позже. */}
              <div className="fw-fld">
                <label htmlFor="fw-last-name">Фамилия</label>
                <input
                  id="fw-last-name"
                  type="text"
                  key={`ln-${me ? me.id : "x"}`}
                  defaultValue={(me && me.name_last) || ""}
                />
              </div>
              <div className="fw-fld">
                <label htmlFor="fw-first-name">Имя</label>
                <input
                  id="fw-first-name"
                  type="text"
                  key={`fn-${me ? me.id : "x"}`}
                  defaultValue={(me && me.name_first) || ""}
                />
              </div>
              <div className="fw-fld">
                <label htmlFor="fw-middle-name">Отчество</label>
                <input
                  id="fw-middle-name"
                  type="text"
                  key={`mn-${me ? me.id : "x"}`}
                  defaultValue={(me && me.name_middle) || ""}
                />
              </div>
              {/* Поля «Дата рождения» здесь больше нет. Такой колонки нет ни у
                  пользователя платформы, ни в форме старой админки — вводить
                  её было некуда, и заполнявший терял данные молча. Появится
                  колонка — вернём поле вместе с сохранением. */}
              <div className="fw-fld">
                <label htmlFor="fw-country">Страна</label>
                <input
                  id="fw-country"
                  type="text"
                  key={`co-${me ? me.id : "x"}`}
                  defaultValue={(me && me.country) || ""}
                />
              </div>
              <div className="fw-fld">
                <label htmlFor="fw-city">Город</label>
                <input
                  id="fw-city"
                  type="text"
                  key={`ct-${me ? me.id : "x"}`}
                  defaultValue={(me && me.town) || ""}
                />
              </div>
            </div>
            {/* Поля «Пол» здесь намеренно нет: оно есть в старой админке, но
                ни на что не влияет, а каждое лишнее поле снижает долю тех,
                кто дозаполняет анкету до конца. Убрано с согласия Максима. */}
          </section>

          <section className="fw-card fw-box">
            <h3>Контакты</h3>
            <p className="fw-note">На почту приходят уведомления и коды подтверждения.</p>
            <div className="fw-grid2">
              <div className="fw-fld">
                <label htmlFor="fw-email">Почта</label>
                {/* Почта — ключ покупателя: по ней приходят награды, и смена
                    означала бы потерю оплаченного. Поэтому только для чтения,
                    как в AI Office. */}
                <input
                  id="fw-email"
                  type="email"
                  key={`em-${me ? me.id : "x"}`}
                  defaultValue={(me && me.email) || email}
                  readOnly
                />
                <p className="fw-fld-hint">
                  Почту изменить нельзя — по ней приходят награды и покупки.
                </p>
              </div>
              <div className="fw-fld">
                <label htmlFor="fw-phone">Телефон</label>
                <input
                  id="fw-phone"
                  type="tel"
                  placeholder="+7…"
                  key={`ph-${me ? me.id : "x"}`}
                  defaultValue={(socials && socials.phone) || ""}
                />
              </div>
              <div className="fw-fld">
                <label htmlFor="fw-telegram">Telegram</label>
                <input
                  id="fw-telegram"
                  type="text"
                  placeholder="@username"
                  key={`tg-${me ? me.id : "x"}`}
                  defaultValue={
                    (me && me.telegram_username) || (socials && socials.telegram) || ""
                  }
                />
                {me && me.is_telegram_linked ? (
                  <p className="fw-fld-hint">Telegram привязан — уведомления приходят туда.</p>
                ) : null}
              </div>
            </div>
            {/* Сохранение через /v1/profile/edit — ту же ручку использует
                старая админка. Раньше здесь стояла оговорка «подключим, когда
                появится ручка»: она появилась раньше, чем оговорку сняли, и
                кабинет год показывал поля, которые нельзя записать. */}
            <div className="fw-form-acts">
              <button
                type="button"
                className="fw-btn"
                disabled={saving || !me}
                onClick={onSaveProfile}
              >
                {saving ? "Сохраняем…" : "Сохранить"}
              </button>
              {saveMsg ? (
                <span className={`fw-save-msg ${saveMsg.ok ? "ok" : "err"}`}>
                  {saveMsg.text}
                </span>
              ) : null}
            </div>
          </section>

          <div className="fw-sec-head">
            <h2>Дополнительно</h2>
          </div>
          <p className="fw-sec-sub">
            Нужно не всем и не сразу: соцсети, файлы, участие в каталогах.
            Вынесено отдельно, чтобы не удлинять анкету.
          </p>
          <section className="fw-card fw-box">
            {/* Соцсети платформа уже отдаёт — показываем заполненные, а не
                обещание «появится позже». Пустые не перечисляем: строка
                «ВКонтакте —» ничего не сообщает. */}
            {socials && SOCIAL_FIELDS.some((f) => socials[f.key]) ? (
              <>
                <h3>Социальные сети</h3>
                <div className="fw-grid2">
                  {SOCIAL_FIELDS.filter((f) => socials[f.key]).map((f) => (
                    <div className="fw-fld" key={f.key}>
                      <label>{f.title}</label>
                      <input type="text" defaultValue={socials[f.key]} readOnly />
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            <ul className="fw-extra-list">
              <li>Мои файлы и сертификаты</li>
              <li>Участие в каталоге экспертов</li>
              <li>Участие на карте</li>
              <li>Реферальная программа</li>
              <li>Язык интерфейса</li>
            </ul>
            <p className="fw-sec-sub mb-0">
              Разделы появятся здесь по мере переноса из старой админки.
            </p>
          </section>
        </div>
      )}

      {tab === "roles" && (
        <div>
          <h2 className="fw-title">Мои роли</h2>
          <p className="fw-sub">
            Роль определяет, какие разделы открыты. Ролей может быть несколько сразу.
            Заявку рассматривает модератор.
          </p>

          <section className="fw-roles-list">
            {ROLES.map((role) => (
              <article className="fw-card fw-role-row" key={role.key}>
                <span className="ic">{role.icon}</span>
                <div>
                  <b>{role.title}</b>
                  <small>{role.short}</small>
                </div>
                <div className="right">
                  {role.granted ? (
                    <span className="fw-tag ok">Открыта</span>
                  ) : (
                    /* Ведёт на описание роли, а не отправляет заявку:
                       человек сначала читает, на что соглашается. */
                    <Link className="fw-btn ghost" to={`${PUBLIC}/role/${role.key}`}>
                      Открыть роль
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </section>
        </div>
      )}

      {/* Реквизиты не дублируем: форма уже написана для раздела документов
          и там же валидируется по типу контрагента. */}
      {tab === "requisites" && <RequisitesForm />}

      {tab === "security" && (
        <div>
          <h2 className="fw-title">Безопасность</h2>
          <p className="fw-sub">
            Пароль от кабинета — он же ваша электронная подпись под документами.
          </p>

          <section className="fw-card fw-box">
            <h3>Смена пароля</h3>
            <p className="fw-note">
              Новый пароль начнёт действовать сразу — на других устройствах
              понадобится войти заново.
            </p>
            <div className="fw-grid2">
              <div className="fw-fld">
                <label htmlFor="fw-pass-current">Текущий пароль</label>
                <input id="fw-pass-current" type="password" autoComplete="current-password" />
              </div>
              <div className="fw-fld">
                <label htmlFor="fw-pass-new">Новый пароль</label>
                <input id="fw-pass-new" type="password" autoComplete="new-password" />
              </div>
            </div>
            <p className="fw-sec-sub mb-0">
              Смену пароля подключим к существующей ручке восстановления.
            </p>
          </section>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
